import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useTenant } from '@/contexts/TenantContext';

// SourcePayload z Valuo formuláře
export interface ValuoSourcePayload {
  source: string;
  formCode?: string;
  createdAt: string;
  /** Výsledek anti-spam scoringu při přijetí formuláře. */
  _spamCheck?: {
    score: number;
    reasons: string[];
    checkedAt: string;
  };
  valuationId?: string;
  propertyId?: string;
  contact?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  property?: {
    type: string;
    address: string;
    addressFull?: string;
    lat?: number;
    lng?: number;
  };
  address?: {
    street?: string;
    streetNumber?: string;
    city?: string;
    district?: string;
    region?: string;
    postalCode?: string;
    country?: string;
  };
  propertyDetails?: {
    floorArea?: number;
    lotArea?: number;
    localType?: string;
    ownership?: string;
    rating?: string;
    houseType?: string;
    landType?: string;
  };
  cadastre?: {
    ruianCode?: string;
    cadastralArea?: string;
    parcelNumber?: string;
    ruianData?: any;
  };
  valuation?: {
    status: string;
    avgPrice?: number;
    minPrice?: number;
    maxPrice?: number;
    avgPriceM2?: number;
    calcArea?: number;
    errorMessage?: string | null;
    requestPayload?: any;
    responsePayload?: any;
  };
  // Hypotéka data
  mortgage?: {
    propertyPrice: number;
    ownFunds: number;
    loanAmount: number;
    loanTerm: number;
    fixation: number;
    interestRate: number;
    monthlyPayment: number;
    totalPayment: number;
    totalInterest: number;
  };
}

export interface Lead {
  id: string;
  tenantId: string;
  contactId: string;
  projectId: string;
  pipelineId: string;
  currentStageId: string | null;
  sourceId: string | null;
  assignedTo: string | null;
  leadType: string;
  status: string;
  expectedValue: number | null;
  notes: string | null;
  sourcePayload?: ValuoSourcePayload;
  createdAt: string;
  updatedAt: string;
  lastActivityAt?: string | null;
  lastStageChangeAt?: string | null;
  lastTouchAt?: string | null;
  contact: {
    id: string;
    firstName: string;
    lastName: string | null;
    email: string | null;
    phone: string | null;
  };
  currentStage?: {
    id: string;
    name: string;
    color: string;
    orderIndex: number;
    probability: number;
  };
  project?: {
    id: string;
    name: string;
  };
  source?: {
    id: string;
    name: string;
    code: string;
  };
}

export interface LeadsResponse {
  data: Lead[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface LeadFilters {
  page?: number;
  limit?: number;
  search?: string;
  stageId?: string;
  sourceId?: string;
  assignedToId?: string;
  projectId?: string;
  status?: string;
  excludeContactId?: string;
}

export type LeadTypeEnum = 'sale' | 'purchase' | 'rent' | 'hypo' | 'invest' | 'pm' | 'other';

export interface CreateLeadDto {
  project_id: string;
  first_name: string;
  last_name?: string;
  email?: string;
  phone?: string;
  lead_type?: LeadTypeEnum;
  expected_value?: number;
  notes?: string;
  source?: string;
  assigned_to?: string;
  pipeline_id?: string;
  stage_id?: string;
}

export interface UpdateLeadDto {
  stageId?: string;
  sourceId?: string;
  assignedToId?: string;
  status?: string;
  expectedValue?: number;
  value?: number;
  probability?: number;
  expectedCloseDate?: string;
  notes?: string;
  leadType?: string;
}

export function useLeads(filters: LeadFilters = {}) {
  const { currentTenant } = useTenant();
  
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.search) params.set('search', filters.search);
  if (filters.stageId) params.set('stageId', filters.stageId);
  if (filters.sourceId) params.set('sourceId', filters.sourceId);
  if (filters.assignedToId) params.set('assignedToId', filters.assignedToId);
  if (filters.projectId) params.set('projectId', filters.projectId);
  if (filters.status) params.set('status', filters.status);
  if (filters.excludeContactId) params.set('excludeContactId', filters.excludeContactId);

  const queryString = params.toString();
  const path = `/leads${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: ['leads', filters, currentTenant?.id],
    queryFn: () => api.get<LeadsResponse>(path),
    enabled: !!currentTenant,
  });
}

export function useLead(id: string) {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['lead', id, currentTenant?.id],
    queryFn: () => api.get<Lead>(`/leads/${id}`),
    enabled: !!id && !!currentTenant,
  });
}

export function useCreateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLeadDto) => api.post<Lead>('/leads', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLeadDto }) =>
      api.patch<Lead>(`/leads/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/leads/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export interface StageHistoryItem {
  id: string;
  leadId: string;
  fromStageId: string | null;
  toStageId: string;
  changedBy: string | null;
  note: string | null;
  changedAt: string;
  fromStage: { id: string; name: string; color: string } | null;
  toStage: { id: string; name: string; color: string };
  user: { id: string; fullName: string } | null;
}

export function useLeadStageHistory(leadId: string) {
  const { currentTenant } = useTenant();
  
  return useQuery({
    queryKey: ['lead-stage-history', leadId, currentTenant?.id],
    queryFn: () => api.get<StageHistoryItem[]>(`/leads/${leadId}/stage-history`),
    enabled: !!leadId && !!currentTenant,
  });
}
